import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { EMPLOYEE_CANDIDATE_SAL, EMPLOYEE_DEPARTMENT, EMPLOYEE_DESIGNATION, EMPLOYEE_GROUP, EMPLOYEE_INTERN_SAL, EMPLOYEESAL, SALARY_TEMPLATE } from "../../../constants/api";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { useLocation } from "react-router-dom";
import { Select, Table } from "antd";
import { salaryTypeOptions } from "../../../constants/enum";
import { cn } from "../../../utils/tailwindUtils";
import { Input } from "../../../components/ui/input";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

// Register the required components
ChartJS.register(ArcElement, Tooltip, Legend);

const calculateAmountFromPercentage = (percentage, base) => {
    if (!percentage || !base) return 0;
    return parseFloat(((percentage / 100) * base).toFixed(2));
};

const regimeType = [
    { label: "Old Regime", value: 'Old Regime' },
    { label: "New Regime", value: 'New Regime' },
];

const init = {
    annual_ctc: 0,
    monthly_ctc: 0,
    salary_type: null,
    earnings: [],
    deductions: [],
    total_earning: {
        percentage: 0,
        monthly_amount: null,
        annual_amount: null
    },
    total_deduction: {
        percentage: 0,
        employee_monthly_amount: null,
        employer_monthly_amount: null,
        annual_amount: 0
    },
    net_salary: 0,
    gross_salary: 0,
    total_deductions_percentage: 0,
    total_deductions_monthly_amount: 0,
    total_deductions_annual_amount: 0,
    net_salary_monthly: 0,
    salary_id: 0,
    is_active: 0,
    salary_template_id: 0,
    gross_monthly_amount: 0,
    gross_annual_amount: 0,
    regime_type: "New Regime"
}

// Helper function to group data
const groupBy = (array, keyGetter) => {
    const map = new Map();
    array.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
};

const toLookupId = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") return value.id ?? null;
    return value;
};

const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const SalaryDetailsContent = forwardRef(({ employeeData = {}, employeeID = null, url = '' }, ref) => {

    // custom methods and dom helpers
    const { fetch, update } = useApiMethods()
    const location = useLocation()

    url = location.pathname.includes('/intern/onboarding/viewDetails')
        ? EMPLOYEE_INTERN_SAL
        : location.pathname.includes('/employee/onboarding/viewDetails')
            ? EMPLOYEE_CANDIDATE_SAL
            : location.pathname.includes('/employee/viewDetails')
                ? EMPLOYEESAL
                : (url || '')

    // prime variables
    const customConfig = JSON.parse(sessionStorage.getItem("customConfig")) || {};
    const is_super_admin = JSON.parse(sessionStorage.getItem("is_super_admin")) || false;
    const is_admin = JSON.parse(sessionStorage.getItem("is_admin")) || false;
    const esicEmployeeLimit = 21000;
    const salary_calculation = "gross";
    let isEditAccess = (is_super_admin || is_admin);

    // state
    const [formData, setFormData] = useState(null)
    const [currentEmployee, setCurrentEmployee] = useState(employeeData)
    const [initialFormData, setInitialFormData] = useState(init)
    const [dropdownData, setDropdownData] = useState({
        designation: [],
        department: [],
        empgroup: [],
        salTemp: []
    })
    const [selectedTemplateData, setSelectedTemplateData] = useState({})
    const [selectedTemplateId, setSelectedTemplateId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [groupedTemplates, setGroupedTemplates] = useState(new Map())

    // input states
    const [salaryType, setSalaryType] = useState(null);
    const [grossAmountMonthly, setGrossAmountMonthly] = useState(null);
    const [grossAmountYearly, setGrossAmountYearly] = useState(null);

    const [earnings, setEarnings] = useState([]);
    const [deductions, setDeductions] = useState([])

    // api calls
    const getEmployeeDepartment = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeDesignation = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_DESIGNATION, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getEmployeeGroup = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_GROUP, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getSalaryTemplate = useCallback(async () => {
        try {
            const apiData = await fetch(SALARY_TEMPLATE, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                const employeeDeptId = toLookupId(employeeData?.professional_info?.department);
                const employeeDesigId = toLookupId(employeeData?.professional_info?.designation);
                const employeeDesigName = employeeData?.professional_info?.designation_name || null;

                const filteredData = apiData.data.filter(item =>
                    (employeeDeptId === null || item?.department?.id === employeeDeptId || !item?.department?.id) &&
                    (employeeDesigId === null || item?.designation?.id === employeeDesigId || !item?.designation?.id) &&
                    (employeeDesigName === null || item?.designation?.designation_name === employeeDesigName || !item?.designation?.designation_name)
                );
                const templates = filteredData.length ? filteredData : apiData.data;

                // Group templates after filtering
                const templateList = groupBy(templates, (data) => {
                    const departmentName = data?.department?.department_name ?? 'N/A';
                    const designationName = data?.designation?.designation_name ?? 'N/A';
                    return `${departmentName} - ${designationName}`;
                });

                setGroupedTemplates(templateList);
                return templates;
            }
            return [];
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch, employeeData]);

    const fetchDropdown = async () => {
        setLoading(true)
        const [designation, department, empgroup, salTemp] = await Promise.all([
            getEmployeeDesignation(),
            getEmployeeDepartment(),
            getEmployeeGroup(),
            getSalaryTemplate()
        ])

        const dropdowns = {
            designation: designation || [],
            department: department || [],
            empgroup: empgroup || [],
            salTemp: salTemp || []
        }

        setDropdownData(dropdowns)
        setLoading(false)

        return dropdowns
    }

    const getEmployeeSalDetails = async (id) => {
        try {
            const response = await fetch(url, { id: id })
            if (response.status === "fail") {
                setFormData(null)
                return
            }
            const salaryRecord = response.data?.result?.[0] || (Array.isArray(response.data) ? response.data[0] : null)
            if (salaryRecord) {
                setFormData(salaryRecord)
            } else {
                setFormData(null)
            }
        } catch (error) {
            ToastMsg('error', error.message)
        }
    }

    useEffect(() => {
        fetchDropdown();
    }, [employeeData?.professional_info?.department, employeeData?.professional_info?.designation]);

    useEffect(() => {
        if (employeeID) {
            getEmployeeSalDetails(employeeID)
        }
    }, [employeeID])

    // Set initial form data
    useEffect(() => {
        if (!formData || Array.isArray(formData)) {
            return
        }
        setGrossAmountMonthly(toNumberOrNull(formData?.gross_salary_monthly_amount));
        setGrossAmountYearly(toNumberOrNull(formData?.gross_salary_annual_amount));

        if (formData?.template) {
            setSelectedTemplateId(formData.template);
            const template = dropdownData.salTemp.find(temp => temp.id === formData.template);
            if (template) {
                setSelectedTemplateData(template);
            }
        }

        if (Array.isArray(formData.earnings) && formData.earnings.length) {
            setEarnings([...formData.earnings]);
        }

        if (Array.isArray(formData.deductions) && formData.deductions.length) {
            setDeductions([...formData.deductions]);
        }

        setSalaryType(formData?.salary_type || null);

        setInitialFormData(prev => ({
            ...prev,
            is_active: formData.is_active,
            salary_id: formData.id,
            salary_type: formData?.salary_type || null,
        }))
    }, [formData, dropdownData.salTemp]);

    const getfilterEmployeeGroupDetails = (employee, employeeGroups) => {
        if (!employee?.professional_info?.employee_group || !Array.isArray(employeeGroups)) return null;
        const groupId = employee.professional_info.employee_group;

        // Find the group that matches the employee's group ID
        const groupDetails = employeeGroups.find(group => group.id === groupId);

        return groupDetails || null;
    };

    const employeeGroupInfo = getfilterEmployeeGroupDetails(currentEmployee, dropdownData.empgroup);

    const getfilterEmployeeSuitableTemplate = (employee, designation, department) => {
        if (!employee?.professional_info?.department ||
            !employee?.professional_info?.designation ||
            !Array.isArray(designation) ||
            !Array.isArray(department) ||
            !dropdownData.salTemp) {
            return null;
        }

        const desigId = toLookupId(employee.professional_info.designation);
        const deptId = toLookupId(employee.professional_info.department);

        // Find the salary template that matches both department and designation
        const suitableTemplate = dropdownData.salTemp.find(template => {
            // Check if template has department and designation objects
            const matchesDepartment = template.department?.id === deptId;
            const matchesDesignation = template.designation?.id === desigId;

            return matchesDepartment && matchesDesignation;
        });

        return suitableTemplate || null;
    };

    // Usage in useEffect
    useEffect(() => {
        if (formData?.template || selectedTemplateId) {
            return;
        }
        if (dropdownData.salTemp && dropdownData.designation && dropdownData.department) {
            const template = getfilterEmployeeSuitableTemplate(
                employeeData,
                dropdownData.designation,
                dropdownData.department
            );

            if (template) {
                handleTemplateChange(template.id);
            }
        }
    }, [dropdownData.salTemp, dropdownData.designation, dropdownData.department, employeeData, formData]);

    // Create options for Select component from grouped templates
    const templateOptions = Array.from(groupedTemplates.entries()).flatMap(([groupName, templates]) => {
        const groupOptions = templates.map(template => ({
            label: `${template.salary_template_name}`,
            value: template.id,
            group: groupName
        }));

        return groupOptions;
    });

    // Alternative: If you want to use OptGroup for better organization
    const templateOptionsWithGroups = Array.from(groupedTemplates.entries()).map(([groupName, templates]) => ({
        label: groupName,
        options: templates.map(template => ({
            label: template.salary_template_name,
            value: template.id,
        }))
    }));

    // onchange
    const handleTemplateChange = (templateId) => {
        setSelectedTemplateId(templateId);

        const template = dropdownData.salTemp.find(temp => temp.id === templateId);
        if (!template) {
            return;
        }
        setSelectedTemplateData(template);

        // Update earnings and deductions based on selected template, but preserve existing IDs and metadata
        if (template) {
            const mergedEarnings = (template.earning_components || []).map(templateEarning => {
                const existingEarning = earnings.find(e => e.name === templateEarning.name);
                return {
                    ...templateEarning,
                    // Preserve existing ID and metadata if it exists
                    ...(existingEarning && {
                        id: existingEarning.id,
                        created_by: existingEarning.created_by,
                        created_date: existingEarning.created_date,
                        modified_by: existingEarning.modified_by,
                        modified_date: existingEarning.modified_date,
                        is_draft: existingEarning.is_draft,
                        salary_id: existingEarning.salary_id,
                        is_active: existingEarning.is_active
                    })
                };
            });
            setEarnings(mergedEarnings);

            // For deductions: merge template data with existing data to preserve IDs
            const mergedDeductions = (template.deduction_components || []).map(templateDeduction => {
                const existingDeduction = deductions.find(d => d.name === templateDeduction.name);
                return {
                    ...templateDeduction,
                    // Preserve existing ID and metadata if it exists
                    ...(existingDeduction && {
                        id: existingDeduction.id,
                        created_by: existingDeduction.created_by,
                        created_date: existingDeduction.created_date,
                        modified_by: existingDeduction.modified_by,
                        modified_date: existingDeduction.modified_date,
                        is_draft: existingDeduction.is_draft,
                        salary_id: existingDeduction.salary_id,
                        is_active: existingDeduction.is_active
                    })
                };
            });
            setDeductions(mergedDeductions);
        }
    };

    const handleGrossAmountChange = (value, type) => {
        const numValue = parseFloat(value) || 0;
        if (type === 'monthly') {
            setGrossAmountMonthly(numValue);
            setGrossAmountYearly(numValue * 12);
        } else {
            setGrossAmountYearly(numValue);
            setGrossAmountMonthly(numValue / 12);
        }
    };

    const earningComponentAmounts = () => {
        if (!grossAmountMonthly || !selectedTemplateData?.earning_components) return {};

        let component = {};

        selectedTemplateData.earning_components.forEach(item => {
            let baseAmount = 0;

            if (item.calcof?.toLowerCase() === 'ctc') {
                baseAmount = grossAmountMonthly;
            } else if (item.calcof?.toLowerCase() === 'basic') {
                // Calculate Basic first if not already calculated
                if (!component['Basic']) {
                    const basicComponent = selectedTemplateData.earning_components.find(e => e.name.toLowerCase() === 'basic');
                    if (basicComponent) {
                        component['Basic'] = calculateAmountFromPercentage(basicComponent.percentage, grossAmountMonthly);
                    }
                }
                baseAmount = component['Basic'] || 0;
            } else {
                baseAmount = grossAmountMonthly;
            }

            component[item.name] = calculateAmountFromPercentage(item.percentage, baseAmount);
        });

        return component;
    }

    const deductionComponentAmounts = () => {
        if (!grossAmountMonthly || !selectedTemplateData?.deduction_components) return {};

        const isDeduction = customConfig?.is_deduction === false;
        const isContractAndNoDeduction =
            employeeGroupInfo?.is_contract_employee === true &&
            customConfig?.deduct_for_contract_emp === false;

        // Return empty if deductions are not applicable
        if (isDeduction || isContractAndNoDeduction) return {};

        let component = {};
        const earnings = earningComponentAmounts();

        selectedTemplateData.deduction_components.forEach((item) => {
            const { name, employee_share_percentage, is_amount, calcof } = item;

            // Determine base amount based on calcof
            let baseAmount = 0;
            if (calcof?.toLowerCase() === "ctc") {
                baseAmount = grossAmountMonthly;
            } else if (earnings[calcof]) {
                baseAmount = earnings[calcof];
            } else {
                baseAmount = grossAmountMonthly;
            }

            // If is_amount is true, then use the amount directly instead of % calculation
            if (is_amount) {
                component[name] = {
                    employeeAmount: item.employee_share_monthly_amount || 0,
                    percentage: null
                };
                return;
            }

            // Special case: PF
            if (name === "PF") {
                const basicPay = earnings.Basic || 0;
                const dearnessAllowance = earnings.DA || 0;
                const totalConsideredAmount = basicPay + dearnessAllowance;
                const pfLimit = customConfig.pf_limit || 15000;
                const capAmount = customConfig?.cap_amount || 0;

                // PF is calculated on the minimum of (total considered amount, PF limit)
                const pfMaxAmount = Math.min(totalConsideredAmount, pfLimit);
                const employeePf = basicPay < pfLimit ? calculateAmountFromPercentage(employee_share_percentage, pfMaxAmount) : capAmount;

                component[name] = {
                    employeeAmount: employeePf,
                    percentage: employee_share_percentage
                };
                return;
            }

            // Special case: ESIC
            if (name === "ESIC") {
                const employeeEsic = grossAmountMonthly <= esicEmployeeLimit
                    ? calculateAmountFromPercentage(employee_share_percentage, grossAmountMonthly)
                    : 0;

                component[name] = {
                    employeeAmount: employeeEsic,
                    percentage: employee_share_percentage
                };
                return;
            }

            // Default case for other deductions
            component[name] = {
                employeeAmount: calculateAmountFromPercentage(employee_share_percentage, baseAmount),
                percentage: employee_share_percentage
            };
        });

        return component;
    }

    const employerContributionAmounts = () => {
        if (!grossAmountMonthly || !selectedTemplateData?.deduction_components) return {};

        const isDeduction = customConfig?.is_deduction === false;
        const isContractAndNoDeduction =
            employeeGroupInfo?.is_contract_employee === true &&
            customConfig?.deduct_for_contract_emp === false;

        // Return empty if deductions are not applicable
        if (isDeduction || isContractAndNoDeduction) return {};

        const component = {};
        const earnings = earningComponentAmounts();

        selectedTemplateData.deduction_components.forEach((item) => {
            const { name, employer_share_percentage, is_amount, calcof } = item;

            // If fixed amount mode, skip percentage calculation
            if (is_amount) {
                component[name] = {
                    employerAmount: item.employer_share_monthly_amount || 0,
                    percentage: null
                };
                return;
            }

            // Determine calculation base from calcof
            let baseAmount = 0;
            if (calcof?.toLowerCase() === "ctc") {
                baseAmount = grossAmountMonthly;
            } else if (earnings[calcof]) {
                baseAmount = earnings[calcof];
            } else {
                baseAmount = grossAmountMonthly;
            }

            // PF logic
            if (name === "PF") {
                const basicPay = earnings.Basic || 0;
                const dearnessAllowance = earnings.DA || 0;
                const totalConsideredAmount = basicPay + dearnessAllowance;
                const pfLimit = customConfig.pf_limit || 15000;
                const capAmount = customConfig?.cap_amount || 0;

                const pfMaxAmount = Math.min(totalConsideredAmount, pfLimit);
                const employerPf = basicPay < pfLimit ? calculateAmountFromPercentage(employer_share_percentage, pfMaxAmount) : capAmount;

                component[name] = {
                    employerAmount: employerPf,
                    percentage: employer_share_percentage
                };
                return;
            }

            // ESIC logic
            if (name === "ESIC") {
                const employerEsic = grossAmountMonthly <= esicEmployeeLimit
                    ? calculateAmountFromPercentage(employer_share_percentage, grossAmountMonthly)
                    : 0;

                component[name] = {
                    employerAmount: employerEsic,
                    percentage: employer_share_percentage
                };
                return;
            }

            // Default case for other deductions
            component[name] = {
                employerAmount: calculateAmountFromPercentage(employer_share_percentage, baseAmount),
                percentage: employer_share_percentage
            };
        });

        return component;
    }

    useEffect(() => {
        const calculatedEarnings = earningComponentAmounts();
        if (!Object.keys(calculatedEarnings).length) {
            return;
        }

        // Step 1: Get all earning keys (merge existing + calculated)
        const allEarningKeys = new Set([
            ...earnings.map((e) => e.name),
            ...Object.keys(calculatedEarnings)
        ]);

        // Step 2: Create a map from current earnings array
        const existingMap = earnings.reduce((acc, item) => {
            acc[item.name] = item;
            return acc;
        }, {});

        // Step 3: Build full array using all keys - PRESERVE ALL ORIGINAL PROPERTIES
        const updated = Array.from(allEarningKeys).map((key) => {
            const monthly = calculatedEarnings[key] || 0;
            const original = selectedTemplateData?.earning_components?.find(e => e.name === key);
            const existingItem = existingMap[key] || {};

            return {
                // Preserve ALL original properties including metadata
                ...existingItem,
                // Then update only the calculated fields
                monthly_amount: monthly,
                annual_amount: monthly * 12,
                percentage: original?.percentage || existingItem.percentage || 0,
                calcof: original?.calcof || existingItem.calcof || null
            };
        });
        setEarnings(updated);
    }, [grossAmountMonthly, selectedTemplateData]);

    useEffect(() => {
        const employeeShare = deductionComponentAmounts();
        const employerShare = employerContributionAmounts();
        if (!Object.keys(employeeShare).length && !Object.keys(employerShare).length) {
            return;
        }

        // Step 1: Get all unique deduction names from both employee & employer
        const allDeductionKeys = new Set([
            ...Object.keys(employeeShare),
            ...Object.keys(employerShare),
        ]);

        // Step 2: Create a map from existing deductions by name
        const existingMap = deductions.reduce((acc, item) => {
            acc[item.name] = item;
            return acc;
        }, {});

        // Step 3: Build the final updated deduction array - PRESERVE ALL ORIGINAL PROPERTIES
        const updated = Array.from(allDeductionKeys).map((key) => {
            const employeeMonthlyAmount = employeeShare[key]?.employeeAmount || 0;
            const employeeMonthlyPercent = employeeShare[key]?.percentage || 0;
            const employerMonthlyAmount = employerShare[key]?.employerAmount || 0;
            const employerMonthlyPercent = employerShare[key]?.percentage || 0;

            const monthly_amount = employeeMonthlyAmount + employerMonthlyAmount;
            const annual_amount = monthly_amount * 12;

            const existingItem = existingMap[key] || {};
            const original = selectedTemplateData?.deduction_components?.find(d => d.name === key);

            return {
                // Preserve ALL original properties including metadata
                ...existingItem,
                // Then update only the calculated fields
                name: original?.name || existingItem.name,
                employee_share_monthly_amount: employeeMonthlyAmount,
                employee_share_annual_amount: employeeMonthlyAmount * 12,
                employee_share_percentage: employeeMonthlyPercent,
                employer_share_monthly_amount: employerMonthlyAmount,
                employer_share_annual_amount: employerMonthlyAmount * 12,
                employer_share_percentage: employerMonthlyPercent,
                monthly_amount: monthly_amount,
                annual_amount: annual_amount,
                // Preserve template properties if they exist
                calcof: original?.calcof || existingItem.calcof || null,
                is_amount: original?.is_amount || existingItem.is_amount || false,
                is_employee_share: original?.is_employee_share !== undefined ? original.is_employee_share : (existingItem.is_employee_share !== undefined ? existingItem.is_employee_share : true),
                is_employer_share: original?.is_employer_share !== undefined ? original.is_employer_share : (existingItem.is_employer_share !== undefined ? existingItem.is_employer_share : true)
            };
        });

        setDeductions(updated);
    }, [grossAmountMonthly, selectedTemplateData]);

    useEffect(() => {
        const totalMonthlyEarnings = earnings.reduce((acc, curr) => {
            return acc + (Number(curr.monthly_amount) || 0);
        }, 0);

        const totalAnnualEarnings = totalMonthlyEarnings * 12;

        const totalEmployeeDeductionMonthly = deductions.reduce((acc, curr) => acc + (curr.employee_share_monthly_amount || 0), 0);
        const totalEmployerDeductionMonthly = deductions.reduce((acc, curr) => acc + (curr.employer_share_monthly_amount || 0), 0);
        const totalDeductionsMonthly = totalEmployeeDeductionMonthly + totalEmployerDeductionMonthly;
        const totalDeductionsAnnual = totalDeductionsMonthly * 12;

        const netMonthly = totalMonthlyEarnings - totalEmployeeDeductionMonthly;
        const netAnnual = netMonthly * 12;

        const totalMonthlyCTC = totalMonthlyEarnings + totalEmployerDeductionMonthly;
        const totalAnnualCTC = totalMonthlyCTC * 12;

        const totalDeductionPercentage = totalMonthlyEarnings
            ? (totalDeductionsMonthly / totalMonthlyEarnings) * 100
            : 0;

        const employeeDeductionPercent = totalMonthlyEarnings
            ? (totalEmployeeDeductionMonthly / totalMonthlyEarnings) * 100
            : 0;

        const finalPayload = {
            ...initialFormData,
            annual_ctc: totalAnnualCTC,
            monthly_ctc: totalMonthlyCTC,
            earnings: earnings,
            deductions: deductions,
            total_earning: {
                percentage: 100,
                monthly_amount: totalMonthlyEarnings,
                annual_amount: totalAnnualEarnings
            },
            total_deduction: {
                percentage: employeeDeductionPercent,
                employee_monthly_amount: totalEmployeeDeductionMonthly,
                employer_monthly_amount: totalEmployerDeductionMonthly,
                annual_amount: totalDeductionsAnnual
            },
            total_deductions_percentage: totalDeductionPercentage,
            total_deductions_monthly_amount: totalDeductionsMonthly,
            total_deductions_annual_amount: totalDeductionsAnnual,
            net_salary: netMonthly,
            net_salary_monthly: netMonthly,
            gross_salary: totalAnnualEarnings,
            gross_monthly_amount: totalMonthlyEarnings,
            gross_annual_amount: totalAnnualEarnings,
            salary_template_id: selectedTemplateData?.id || 0,
            // Both save branches spread initialFormData, so carrying it here reaches
            // the payload without touching either of them.
            salary_type: salaryType || null
        };

        setInitialFormData(finalPayload);
    }, [earnings, deductions, grossAmountMonthly, selectedTemplateData, salaryType]);

    // submit call
    useImperativeHandle(ref, () => ({
        handleSaveSalDetails: async () => {
            if (salary_calculation === "gross") {

                let updated = {
                    ...initialFormData,
                    annual_ctc: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount * 12 : initialFormData.annual_ctc,
                    monthly_ctc: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.monthly_ctc,
                    net_salary: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.net_salary,
                    net_salary_monthly: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.net_salary_monthly,
                    deductions: employeeGroupInfo?.is_contract_employee === true && customConfig?.deduct_for_contract_emp === false ? [] : initialFormData.deductions,
                    total_deductions_monthly_amount: employeeGroupInfo?.is_contract_employee === true ? 0 : initialFormData.total_deductions_monthly_amount,
                    earnings: earnings,
                };

                let updateFormData = { ...updated }
                updateFormData["employee_id"] = employeeID;
                updateFormData["salary_id"] = formData?.id ?? formData?.salary_info?.salary_id ?? null;
                updateFormData["is_active"] = formData?.is_active ?? formData?.salary_info?.is_active ?? true;
                updateFormData["gross_salary_percentage"] = updateFormData?.total_earning?.percentage ? updateFormData?.total_earning?.percentage : 0;
                updateFormData["gross_salary_monthly_amount"] = Number(grossAmountMonthly) || Number(updateFormData.gross_monthly_amount) || 0;
                updateFormData["gross_salary_annual_amount"] = Number(grossAmountYearly) || Number(updateFormData.gross_annual_amount) || 0;
                updateFormData["template"] = selectedTemplateId || updateFormData?.salary_template_id || "";
                updateFormData["total_deductions_percentage"] = updateFormData?.total_deductions_percentage || updateFormData?.total_deduction?.percentage || 0;
                updateFormData["total_deductions_monthly_amount"] = updateFormData?.total_deductions_monthly_amount || 0;
                updateFormData["total_deductions_annual_amount"] = updateFormData?.total_deductions_annual_amount || updateFormData?.total_deduction?.annual_amount || 0;
                if (!Number(updateFormData.annual_ctc) && updateFormData.gross_salary_monthly_amount) {
                    const employerMonthly = (deductions || []).reduce((acc, curr) => acc + (Number(curr.employer_share_monthly_amount) || 0), 0);
                    updateFormData.monthly_ctc = updateFormData.gross_salary_monthly_amount + employerMonthly;
                    updateFormData.annual_ctc = updateFormData.monthly_ctc * 12;
                }
                delete updateFormData.total_earning;
                delete updateFormData.total_deduction;
                delete updateFormData.gross_salary;
                delete updateFormData.gross_monthly_amount;
                delete updateFormData.gross_annual_amount;
                delete updateFormData.salary_template_id;

                try {
                    const response = await update(url, updateFormData, false)

                    if (response.status !== 'fail') {
                        console.log(response.message);
                        // ToastMsg('success', response.message)
                    } else {
                        ToastMsg('error', response.message)
                        return false
                    }
                } catch (error) {
                    ToastMsg('error', error.message)
                    return false
                }
                // Add your actual save logic here
            } else if (salary_calculation === "ctc") {
                console.log("Saving salary details with CTC calculation", initialFormData);
                // Add your actual save logic here
            }
        }
    }))

    // loader
    if (loading) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    // table columns
    const earningColumns = [
        {
            title: "Description",
            dataIndex: "name",
            key: "name",
            render: (text) => <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{text}</span>,
            className: "bg-green-50 dark:bg-green-900/20"
        },
        {
            title: "Percentage",
            dataIndex: "percentage",
            key: "percentage",
            render: (text) => <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{text}%</span>,
            className: "bg-blue-50 dark:bg-blue-900/20",
            align: 'center'
        },
        {
            title: "Monthly Amount",
            dataIndex: "monthly_amount",
            key: "monthly_amount",
            render: (amount) => <span className="text-sm font-semibold text-green-600 dark:text-green-400">₹{amount?.toLocaleString('en-IN') || 0}</span>,
            className: "bg-emerald-50 dark:bg-emerald-900/20",
            align: 'right'
        },
        {
            title: "Annual Amount",
            dataIndex: "annual_amount",
            key: "annual_amount",
            render: (amount) => <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">₹{amount?.toLocaleString('en-IN') || 0}</span>,
            className: "bg-violet-50 dark:bg-violet-900/20",
            align: 'right'
        },
    ];

    const deductionColumns = [
        {
            title: "Description",
            dataIndex: "name",
            key: "name",
            render: (text) => <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{text}</span>,
            className: "bg-red-50 dark:bg-red-900/20"
        },
        {
            title: "Percentage",
            dataIndex: "employee_share_percentage",
            key: "percentage",
            render: (text) => <span className="text-sm font-medium text-orange-600 dark:text-orange-400">{text}%</span>,
            className: "bg-amber-50 dark:bg-amber-900/20",
            align: 'center'
        },
        {
            title: "Monthly Amount",
            dataIndex: "employee_share_monthly_amount",
            key: "employee_share_monthly_amount",
            render: (amount) => <span className="text-sm font-semibold text-red-600 dark:text-red-400">₹{amount?.toLocaleString('en-IN') || 0}</span>,
            className: "bg-rose-50 dark:bg-rose-900/20",
            align: 'right'
        },
        {
            title: "Annual Amount",
            dataIndex: "employee_share_annual_amount",
            key: "employee_share_annual_amount",
            render: (amount) => <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">₹{amount?.toLocaleString('en-IN') || 0}</span>,
            className: "bg-pink-50 dark:bg-pink-900/20",
            align: 'right'
        },
    ];

    // preview chart
    const earningsData = earningComponentAmounts() || {};
    const deductionsData = deductionComponentAmounts() || {};
    const contributionData = employerContributionAmounts() || {};
    const hasData = Object.keys(earningsData).length > 0 || Object.keys(deductionsData).length > 0;

    const chartData = {
        labels: hasData
            ? [
                ...Object.keys(earningsData).map((key) => key.toUpperCase()),
                ...Object.keys(deductionsData).map((key) => key.toUpperCase())
            ]
            : ["No Data"],

        datasets: [
            {
                label: "Salary Components",
                data: hasData
                    ? [
                        ...Object.values(earningsData),
                        ...Object.values(deductionsData).map((item) =>
                            typeof item === 'object' ? item.employeeAmount || 0 : item
                        )
                    ]
                    : [0],

                backgroundColor: [
                    "#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40",
                    "#8BC34A", "#F44336", "#00BCD4", "#FFEB3B", "#E91E63", "#3F51B5"
                ],
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${label}: ₹${value.toLocaleString()} (${percentage}%)`;
                    }
                }
            }
        }
    };

    // Calculate summary values
    const totalMonthlyEarnings = earnings.reduce((acc, curr) => acc + (curr.monthly_amount || 0), 0);
    const totalEmployeeDeduction = deductions.reduce((acc, curr) => acc + (curr.employee_share_monthly_amount || 0), 0);
    const totalEmployerContribution = deductions.reduce((acc, curr) => acc + (curr.employer_share_monthly_amount || 0), 0);

    const netPay = totalMonthlyEarnings - totalEmployeeDeduction;
    const monthlyCTCIncludingPF = totalMonthlyEarnings + totalEmployerContribution;
    const yearlyCTCIncludingPF = monthlyCTCIncludingPF * 12;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Salary Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Complete employee Salary break downs and deduction details
                    </p>
                </div>
                <div className="flex items-end gap-3">
                    <div className="space-y-2 flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            Salary Type :
                        </label>
                        <Select
                            size="large"
                            value={salaryType}
                            placeholder="Select Salary Type"
                            options={salaryTypeOptions}
                            onChange={(value) => setSalaryType(value || null)}
                            allowClear
                            className={cn(
                                "w-44 rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20"
                            )}
                        />
                    </div>
                    <div className="space-y-2 flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            Select Template :
                        </label>
                        <Select
                            showSearch
                            size="large"
                            value={selectedTemplateId}
                            placeholder="Select Template"
                            options={templateOptionsWithGroups}
                            onChange={handleTemplateChange}
                            filterOption={(input, option) => {
                                const label = option?.label || option?.children;
                                return label?.toLowerCase().includes(input.toLowerCase());
                            }}
                            className={cn(
                                "w-64 rounded-md transition-colors duration-200", // Added fixed width
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>
                </div>
            </div>

            {/* earnings and deduction table section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* first card */}
                <div className="lg:col-span-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="p-4">
                        {/* amount fields */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Monthly Gross
                                </label>
                                <Input
                                    type="number"
                                    value={grossAmountMonthly}
                                    onChange={(e) => handleGrossAmountChange(e.target.value, 'monthly')}
                                    placeholder="Enter Monthly gross"
                                    disabled={loading}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Yearly Gross
                                </label>
                                <Input
                                    type="number"
                                    value={grossAmountYearly}
                                    onChange={(e) => handleGrossAmountChange(e.target.value, 'yearly')}
                                    placeholder="Enter Year gross"
                                    disabled={loading}
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                />
                            </div>
                        </div>

                        {/* table */}
                        <div className="flex flex-col gap-6">
                            {/* Earnings Table */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <TrendingUp className="w-5 h-5 text-green-500" />
                                            <label className="text-sm font-medium text-gray-900 dark:text-white">Earnings</label>
                                        </div>
                                        <span className="text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-600 px-2 py-1 rounded-md">
                                            {earnings?.length || 0} components
                                        </span>
                                    </div>
                                </div>
                                <div className="p-1">
                                    <Table
                                        columns={earningColumns}
                                        dataSource={earnings}
                                        pagination={false}
                                        size="middle"
                                        className="custom-table"
                                        rowKey="id"
                                        rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                                    />
                                </div>
                            </div>

                            {/* Deductions Table */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <TrendingDown className="w-5 h-5 text-red-500" />
                                            <label className="text-sm font-medium text-gray-900 dark:text-white">Deductions</label>
                                        </div>
                                        {customConfig?.is_deduction !== false && employeeGroupInfo?.is_contract_employee !== true && (
                                            <span className="text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-600 px-2 py-1 rounded-md">
                                                {deductions?.length || 0} components
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-1">
                                    {customConfig?.is_deduction === false ? (
                                        // Case 1: Global Deductions turned off 
                                        <div className="py-10 text-center">
                                            <div className="text-gray-500 dark:text-gray-400">
                                                <svg className="mx-auto h-14 w-14 mb-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                <p className="text-sm font-medium mb-1">Deductions Not Applicable</p>
                                                <p className="text-xs max-w-md mx-auto leading-relaxed">
                                                    Deductions are not applicable as per company policy. You will receive your full gross pay.
                                                </p>
                                            </div>
                                        </div>
                                    ) : employeeGroupInfo?.is_contract_employee === true &&
                                        customConfig?.deduct_for_contract_emp === false ? (
                                        // Case 2: Contract employee but no deductions
                                        <div className="py-10 text-center">
                                            <div className="text-gray-500 dark:text-gray-400">
                                                <svg className="mx-auto h-14 w-14 mb-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <p className="text-sm font-medium mb-1">Contract Employee Benefits</p>
                                                <p className="text-xs max-w-md mx-auto leading-relaxed">
                                                    Deductions are not applicable for contract employees as per company policy. You will receive your full gross pay.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        // Case 3: Deductions are applicable
                                        <>
                                            <Table
                                                columns={deductionColumns}
                                                dataSource={deductions}
                                                pagination={false}
                                                size="middle"
                                                className="custom-table"
                                                rowKey="id"
                                                rowClassName="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Note */}
                        <div className="mt-6 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-3">
                                        Important Notes
                                    </h4>
                                    <ul className="space-y-1">
                                        <li className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200">
                                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                                            <span>
                                                <span className="font-medium">Net Salary</span> excludes employer contributions and represents take-home pay.
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200">
                                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                                            <span>
                                                <span className="font-medium">ESIC</span> will apply only if the monthly gross salary is <span className="font-semibold text-blue-900 dark:text-blue-100">₹{esicEmployeeLimit?.toLocaleString()}</span> or below.
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200">
                                            <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full"></span>
                                            <span>
                                                <span className="font-medium">PF</span> is calculated based on Basic salary up to <span className="font-semibold text-blue-900 dark:text-blue-100">₹{(customConfig?.pf_limit || 15000)?.toLocaleString()}</span> as per statutory limits.
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* second card */}
                <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="p-4">
                        <div className="flex flex-col space-y-6">
                            {/* Chart Container */}
                            <div className="flex justify-center">
                                <div className="relative" style={{ width: '280px', height: '280px' }}>
                                    <Pie data={chartData} options={chartOptions} />
                                    {/* Chart Center Info */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-center">
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Total</p>
                                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                ₹{totalMonthlyEarnings.toLocaleString('en-IN')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Salary Summary Cards - Enhanced Design */}
                            <div className="space-y-4">
                                {/* Net Pay - Highlighted */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-green-100 dark:bg-green-800 rounded-lg">
                                                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-green-800 dark:text-green-200">Net Pay Salary</p>
                                                <p className="text-xs text-green-600 dark:text-green-400">Your take home amount</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-green-900 dark:text-green-100">
                                                ₹{netPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                            <p className="text-xs text-green-700 dark:text-green-300 mt-1">Monthly</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Two Column Grid for Other Metrics */}
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Total Deduction */}
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-red-800 dark:text-red-200">Deductions</p>
                                                </div>
                                                <p className="text-xs text-red-600 dark:text-red-400">From your gross</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-red-900 dark:text-red-100">
                                                ₹{totalEmployeeDeduction.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Monthly CTC */}
                                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-blue-800 dark:text-blue-200">Monthly CTC</p>
                                                </div>
                                                <p className="text-xs text-blue-600 dark:text-blue-400">With employer shares</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-blue-900 dark:text-blue-100">
                                                ₹{monthlyCTCIncludingPF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Yearly CTC */}
                                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-purple-800 dark:text-purple-200">Yearly CTC</p>
                                                </div>
                                                <p className="text-xs text-purple-600 dark:text-purple-400">Annual cost to company</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-purple-900 dark:text-purple-100">
                                                ₹{yearlyCTCIncludingPF.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Gross Salary */}
                                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                                                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Gross Salary</p>
                                                </div>
                                                <p className="text-xs text-amber-600 dark:text-amber-400">Before deductions</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-md font-bold text-amber-900 dark:text-amber-100">
                                                ₹{totalMonthlyEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Employer Contributions - Enhanced */}
                            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
                                            <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xs font-semibold text-amber-800 dark:text-amber-200">Employer Contributions</h3>
                                            <p className="text-xs text-amber-600 dark:text-amber-400">Additional benefits provided</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-md font-bold text-amber-900 dark:text-amber-100">
                                            ₹{totalEmployerContribution.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </p>
                                        <p className="text-xs text-amber-700 dark:text-amber-300">Total monthly</p>
                                    </div>
                                </div>

                                {Object.keys(contributionData || {}).length > 0 ? (
                                    <div className="space-y-3">
                                        {Object.keys(contributionData).map((key) => (
                                            <div key={key} className="flex items-center justify-between p-3 bg-white dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-700/50">
                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 capitalize">
                                                            {key.replace(/_/g, ' ')}
                                                        </p>
                                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                                            {contributionData[key]?.percentage || 0}% of base
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="text-sm font-semibold text-amber-900 dark:text-amber-100 whitespace-nowrap ml-3">
                                                    ₹{(contributionData[key]?.employerAmount || 0).toLocaleString('en-IN', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6">
                                        <svg className="w-12 h-12 text-amber-300 dark:text-amber-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">No employer contributions</p>
                                        <p className="text-xs text-amber-500 dark:text-amber-500 mt-1">Configure deductions to see contributions</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
})

export default SalaryDetailsContent;